import React, { useEffect, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import Landing from './page/Landing';
import Billboard from './page/Billboard';
import Projects from './page/Projects';
import ProjectDetail from './page/ProjectDetail';
import Works from './page/Works';
import Texts from './page/Texts';
import Members from './page/Members';
import About from './page/About';
import Contact from './page/Contact';
import Settings from './page/Settings';
import ErrorPage from './page/Error';
import { useGlobalError } from './shared/hooks/useGlobalError';
import { usePortalAutoAction } from './shared/hooks/usePortalAutoAction';
import { useDeviceDetection } from './shared/hooks/useDeviceDetection';
import { useGlobalSystemAlert } from './shared/hooks/useGlobalSystemAlert';
import { usePasswordStatusCheck } from './shared/hooks/usePasswordCheck';
import LeftNavBar from './component/floatingIsland/LeftNavBar';
import LeftNavBarFixed from './component/floatingIsland/fixed/LeftNavBarFixed';
import RightTopIsland from './component/floatingIsland/RightTopIsland';
import RightBottomIsland from './component/floatingIsland/RightBottomIsland';
import LeftTopIsland from './component/floatingIsland/LeftTopIsland';
import LeftNavBarMobile from './component/floatingIsland/mobile/LeftNavBarMobile';
import MobileConvertWrapper from './shared/component/MobileConvertWrapper';
import MobileTabletConvertWrapper from './shared/component/MobileTabletConvertWrapper';
import Admin from './page/Admin';
import Exhibitions from './page/Exhibitions';
import Privacy from './page/policy/Privacy';
import Terms from './page/policy/Terms';
import LeftBottomIsland from './component/floatingIsland/LeftBottomIsland';
import Copyright from './component/floatingIsland/Copyright';
import MobileBanWrapper from './shared/component/MobileBanWrapper';
import Poll from './page/poll/Poll';
import GuideOrchestrator from './component/guide/GuideOrchestrator';
import { useLoadingStore } from './shared/store/loadingStore';
import Loading from './shared/component/Loading';
import LeftBottomIslandMobile from './component/floatingIsland/mobile/LeftBottomIslandMobile';
import { paperBackgroundUrl } from './shared/component/paperTexture';
import { useLanguage } from './shared/hooks/useLanguage';
import { useClarityAdminGuard } from './shared/hooks/useClarityAdminGuard';
import ArchiveRouteGuard from './shared/component/ArchiveRouteGuard';
import { useAuth } from './shared/hooks/useApi/useAuth';
import { useAuthTokenStore } from './shared/store/authStore';
import { useNavLayout } from './shared/hooks/useNavLayout';
import { NavLayout } from './shared/enum/EnumRepository';
import ModalTemplate from './shared/ModalTemplate';
import AlertTemplate from './shared/AlertTemplate';
import TempAlertTemplate from './shared/TempAlertTemplate';


const App: React.FC = () => {
  useGlobalError();
  usePortalAutoAction();
  useDeviceDetection();
  useGlobalSystemAlert();
  usePasswordStatusCheck();
  useLanguage();
  useClarityAdminGuard();
  const { isLoading } = useLoadingStore();
  const { user, refresh } = useAuth();
  const authToken = useAuthTokenStore((s) => s.authToken);
  const navLayout = useNavLayout();
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (user && !authToken) {
      void refreshRef.current();
    }
  }, [user, authToken]);

  return (
    <AppComp className="App" $backgroundImg={paperBackgroundUrl} $navLayout={navLayout}>
      <Loading isLoading={isLoading > 0} />
      <MobileTabletConvertWrapper
        onlyMobileChildren={<LeftNavBarMobile />}
        onlyNotMobileChildren={
          navLayout === NavLayout.FIXED ? <LeftNavBarFixed /> : <LeftNavBar />
        }
      />
      <LeftTopIsland />
      <RightTopIsland />
      <RightBottomIsland />
      <PageSlot $navLayout={navLayout}>
      <Routes>
        {/* Common routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/policy/privacy" element={<Privacy />} />
        <Route path="/policy/terms" element={<Terms />} />
        <Route path="/exhibitions" element={<Exhibitions />} />
        {/* /:HANDLE/members보다 우선 매칭되도록 정적 경로를 명시 */}
        <Route path="/admin/members" element={<Admin />} />
        <Route path="/admin/*" element={<Admin />} />

        <Route path="/onboard" element={<Navigate to="/" replace />} />

        {/* HANDLE routes (AUI도 지원하지만, AUI는 추후 삭제 예정)*/}
        <Route element={<ArchiveRouteGuard />}>
          <Route path="/:HANDLE" element={<Billboard />} />
          <Route path="/:HANDLE/projects" element={<Projects />} />
          <Route path="/:HANDLE/projects/:projectId" element={<ProjectDetail />} />
          <Route path="/:HANDLE/works" element={<Works />} />
          <Route path="/:HANDLE/texts" element={<Texts />} />
          <Route path="/:HANDLE/members" element={<Members />} />
          <Route path="/:HANDLE/about" element={<About />} />
          <Route path="/:HANDLE/contact" element={<Contact />} />
          <Route path="/:HANDLE/settings" element={<Settings />} />
          <Route path="/:HANDLE/poll" element={<Poll />} />
        </Route>

        <Route path="/error" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
      </PageSlot>
      <MobileBanWrapper>
        <GuideOrchestrator />
      </MobileBanWrapper>
      <MobileConvertWrapper
        onlyMobileChildren={<LeftBottomIslandMobile />}
        onlyNotMobileChildren={
          <>
            <LeftBottomIsland />
            <Copyright />
          </>
        } />
      {/* Router 안에서 useLocation 등 사용 (ImportStation canShow 등) */}
      <ModalTemplate />
      <AlertTemplate />
      <TempAlertTemplate />
    </AppComp>
  );
}


const AppComp = styled.div<{ $backgroundImg: string; $navLayout: NavLayout }>`
  position: relative;
  height: 100vh;
  height: 100dvh;

  display: flex;
  flex-direction: ${({ $navLayout }) =>
    $navLayout === NavLayout.FIXED ? 'row' : 'column'};
  align-items: center;
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${({ $backgroundImg }) => $backgroundImg});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.3;
    z-index: -1;
  }
`;

const PageSlot = styled.div<{ $navLayout: NavLayout }>`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: ${({ $navLayout }) =>
    $navLayout === NavLayout.FIXED ? 'flex' : 'contents'};
  justify-content: center;
`;

export default App;
